import React, { useState, useEffect } from 'react';
import { Heart, Star, ShieldAlert, Plus, Send, Sparkles, MessageCircle, Trash2, Copy, CheckCircle, Mail, TrendingUp, AlertTriangle } from 'lucide-react';
import Satisfaccion360 from './Satisfaccion360';
import { motion } from 'framer-motion';
import Sidebar from '../../components/Sidebar';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../utils/api';

const PREGUNTAS_DEFAULT = [
  'Satisfacción General',
  'Rapidez en las respuestas',
  'Comunicación del equipo',
  'Calidad técnica del servicio'
];

const PostVenta = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('satisfaccion360');
  const [campanas, setCampanas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCampana, setShowNewCampana] = useState(false);
  const [selectedCampana, setSelectedCampana] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [savingResumen, setSavingResumen] = useState(false);
  const [newCampana, setNewCampana] = useState({
    nombre: '',
    asunto: '',
    cuerpo: 'Estimad@ cliente,\n\nEn Peritum queremos mejorar continuamente. Te invitamos a responder esta breve encuesta de 2 minutos para conocer tu experiencia con nuestros servicios.\n\nGracias por tu tiempo.',
    filtro_destinatarios: 'activos',
    preguntas: [...PREGUNTAS_DEFAULT]
  });

  useEffect(() => {
    fetchCampanas();
  }, []);

  const fetchCampanas = async () => {
    try {
      setLoading(true);
      const res = await api.get('/post-venta/campanas');
      setCampanas(res.data.campanas || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampana = async () => {
    if (!newCampana.nombre.trim() || !newCampana.asunto.trim() || !newCampana.cuerpo.trim()) {
      toast.error('Completa nombre, asunto y cuerpo');
      return;
    }
    const preguntasLimpias = (newCampana.preguntas || []).map(p => p.trim()).filter(Boolean);
    if (preguntasLimpias.length === 0) {
      toast.error('Agrega al menos 1 pregunta');
      return;
    }
    if (preguntasLimpias.length > 10) {
      toast.error('Máximo 10 preguntas por encuesta');
      return;
    }
    try {
      const res = await api.post('/post-venta/campanas', {
        ...newCampana,
        preguntas: preguntasLimpias
      });
      toast.success(res.data.message);
      setShowNewCampana(false);
      setNewCampana({ ...newCampana, nombre: '', asunto: '', preguntas: [...PREGUNTAS_DEFAULT] });
      fetchCampanas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando campaña');
    }
  };

  const handleOpenDetalle = async (campana) => {
    setSelectedCampana(campana);
    try {
      const res = await api.get(`/post-venta/campanas/${campana.id}`);
      setDetalle(res.data);
    } catch (e) {
      toast.error('Error cargando detalle');
    }
  };

  const handleGenerarResumen = async () => {
    if (!selectedCampana) return;
    setSavingResumen(true);
    try {
      const res = await api.post(`/post-venta/campanas/${selectedCampana.id}/ai-resumen`);
      setDetalle({ ...detalle, campana: { ...detalle.campana, ai_resumen: res.data.ai_resumen } });
      toast.success('Resumen IA generado');
    } catch (e) {
      toast.error('Error generando resumen IA');
    } finally {
      setSavingResumen(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta campaña y todas sus respuestas?')) return;
    try {
      await api.delete(`/post-venta/campanas/${id}`);
      toast.success('Campaña eliminada');
      setDetalle(null);
      setSelectedCampana(null);
      fetchCampanas();
    } catch (e) {
      toast.error('Error');
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/encuesta/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="post-venta-page">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className="flex-1 p-8 overflow-y-auto max-h-screen">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-10 h-10 text-rose-500" strokeWidth={1.5} />
            <h1 className="text-4xl font-bold text-slate-900">Post Venta</h1>
          </div>
          <p className="text-lg text-slate-600 mb-6">
            Fidelización, encuestas de satisfacción y seguimiento de renovaciones.
          </p>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 gap-1 h-auto p-1">
              <TabsTrigger value="satisfaccion360" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-rose-500 data-[state=active]:text-white" data-testid="tab-satisfaccion360">
                <ShieldAlert className="w-3.5 h-3.5" />
                Satisfacción 360° (Post Venta)
              </TabsTrigger>
              <TabsTrigger value="encuestas" className="flex items-center justify-center gap-1 text-xs px-2 py-2 data-[state=active]:bg-rose-500 data-[state=active]:text-white" data-testid="tab-encuestas">
                <Star className="w-3.5 h-3.5" />
                Encuestas de Satisfacción
              </TabsTrigger>
            </TabsList>

            {/* ===== SATISFACCIÓN 360° ===== */}
            <TabsContent value="satisfaccion360">
              <Satisfaccion360 />
            </TabsContent>

            {/* ===== ENCUESTAS ===== */}
            <TabsContent value="encuestas">
              {!detalle ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-slate-800">Campañas de Encuesta</h2>
                    <Button className="bg-rose-500 hover:bg-rose-600" onClick={() => setShowNewCampana(true)} data-testid="new-campana-btn">
                      <Plus className="w-4 h-4 mr-1" /> Nueva Campaña
                    </Button>
                  </div>

                  {loading ? (
                    <Card className="p-12 text-center"><p className="text-slate-500">Cargando...</p></Card>
                  ) : campanas.length === 0 ? (
                    <Card className="p-12 text-center border-slate-200">
                      <Star className="w-16 h-16 text-rose-300 mx-auto mb-4" strokeWidth={1.5} />
                      <p className="text-lg text-slate-700 mb-2">Aún no hay campañas</p>
                      <p className="text-sm text-slate-500">Crea tu primera campaña para empezar a medir la satisfacción de tus clientes.</p>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {campanas.map((c) => (
                        <Card key={c.id} className="p-5 border-rose-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleOpenDetalle(c)} data-testid={`campana-card-${c.id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-900 text-base">{c.nombre}</h3>
                              <p className="text-xs text-slate-500">{new Date(c.fecha_envio).toLocaleDateString('es-CL')}</p>
                            </div>
                            <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">
                              {c.filtro_destinatarios}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-xs text-slate-500">Enviadas</p>
                              <p className="text-xl font-bold text-slate-700">{c.total_destinatarios}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Respondidas</p>
                              <p className="text-xl font-bold text-emerald-600">{c.total_respondidas}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Tasa</p>
                              <p className="text-xl font-bold text-rose-600">{c.tasa_respuesta}%</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <DetalleCampana
                  detalle={detalle}
                  onBack={() => { setDetalle(null); setSelectedCampana(null); }}
                  onGenerarResumen={handleGenerarResumen}
                  savingResumen={savingResumen}
                  onDelete={() => handleDelete(detalle.campana.id)}
                  onCopyLink={copyLink}
                />
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Modal Nueva Campaña */}
      <Dialog open={showNewCampana} onOpenChange={setShowNewCampana}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-rose-500" /> Nueva Campaña de Encuesta
            </DialogTitle>
            <DialogDescription>
              Define el contenido del mail y los destinatarios. Cada cliente recibirá un link único para responder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre interno *</Label>
              <Input
                placeholder="Ej: Encuesta Q1 2026"
                value={newCampana.nombre}
                onChange={(e) => setNewCampana({ ...newCampana, nombre: e.target.value })}
                data-testid="campana-nombre-input"
              />
            </div>
            <div>
              <Label>Asunto del mail *</Label>
              <Input
                placeholder="Ej: ¿Cómo evaluas nuestro servicio?"
                value={newCampana.asunto}
                onChange={(e) => setNewCampana({ ...newCampana, asunto: e.target.value })}
                data-testid="campana-asunto-input"
              />
            </div>
            <div>
              <Label>Cuerpo del mail *</Label>
              <Textarea
                rows={6}
                value={newCampana.cuerpo}
                onChange={(e) => setNewCampana({ ...newCampana, cuerpo: e.target.value })}
                data-testid="campana-cuerpo-input"
              />
            </div>
            <div>
              <Label>Destinatarios *</Label>
              <Select value={newCampana.filtro_destinatarios} onValueChange={(v) => setNewCampana({ ...newCampana, filtro_destinatarios: v })}>
                <SelectTrigger data-testid="campana-filtro-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activos">Clientes Activos</SelectItem>
                  <SelectItem value="inactivos">Clientes Inactivos</SelectItem>
                  <SelectItem value="todos">Todos los clientes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Preguntas editables */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label>Preguntas de la encuesta</Label>
                  <p className="text-xs text-slate-500">
                    Cada pregunta se evaluará con 1 a 5 estrellas. La <strong>primera pregunta</strong> dispara el trigger NPS (si nota ≤ 2 se crea automáticamente una tarea "Revisión de Caso").
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={(newCampana.preguntas?.length || 0) >= 10}
                  onClick={() => setNewCampana({
                    ...newCampana,
                    preguntas: [...(newCampana.preguntas || []), '']
                  })}
                  data-testid="add-pregunta-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Agregar pregunta
                </Button>
              </div>
              <div className="space-y-2 max-h-[260px] overflow-y-auto bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="preguntas-editor">
                {(newCampana.preguntas || []).map((p, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className={`text-xs font-mono px-2 py-1 rounded ${idx === 0 ? 'bg-amber-200 text-amber-800' : 'bg-slate-200 text-slate-700'}`}>
                      P{idx + 1}{idx === 0 ? ' ★' : ''}
                    </span>
                    <Input
                      value={p}
                      onChange={(e) => {
                        const next = [...newCampana.preguntas];
                        next[idx] = e.target.value;
                        setNewCampana({ ...newCampana, preguntas: next });
                      }}
                      placeholder={`Pregunta ${idx + 1}`}
                      className="flex-1"
                      data-testid={`pregunta-input-${idx}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                      onClick={() => {
                        if ((newCampana.preguntas?.length || 0) <= 1) {
                          toast.error('Debe haber al menos 1 pregunta');
                          return;
                        }
                        const next = newCampana.preguntas.filter((_, i) => i !== idx);
                        setNewCampana({ ...newCampana, preguntas: next });
                      }}
                      data-testid={`remove-pregunta-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Además de estas preguntas, todos los clientes pueden dejar un comentario libre al final.
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowNewCampana(false)}>Cancelar</Button>
              <Button className="bg-rose-500 hover:bg-rose-600" onClick={handleCreateCampana} data-testid="campana-create-confirm-btn">
                <Send className="w-4 h-4 mr-1" /> Crear y generar links
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetalleCampana = ({ detalle, onBack, onGenerarResumen, savingResumen, onDelete, onCopyLink }) => {
  const { campana, stats, nube_palabras, respuestas, pendientes } = detalle;
  const maxCount = nube_palabras[0]?.count || 1;

  return (
    <div className="space-y-5" data-testid="detalle-campana">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack}>← Volver</Button>
          <h2 className="text-2xl font-bold text-slate-900 mt-2">{campana.nombre}</h2>
          <p className="text-sm text-slate-500">{campana.asunto}</p>
        </div>
        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={onDelete}>
          <Trash2 className="w-4 h-4 mr-1" /> Eliminar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="campana-stats">
        <Card className="p-4 border-emerald-200 bg-emerald-50">
          <p className="text-xs uppercase text-emerald-700 font-semibold">Satisfacción</p>
          <p className="text-3xl font-bold text-emerald-700">{stats.porcentaje_satisfaccion}%</p>
          <p className="text-xs text-emerald-600">Promotores ≥ 4/5 en P1</p>
        </Card>
        <Card className="p-4 border-slate-200">
          <p className="text-xs uppercase text-slate-600 font-semibold">Promedio general (P1)</p>
          <p className="text-3xl font-bold text-slate-700">{stats.promedio_general}/5</p>
        </Card>
        <Card className="p-4 border-rose-200 bg-rose-50">
          <p className="text-xs uppercase text-rose-700 font-semibold">Tasa de respuesta</p>
          <p className="text-3xl font-bold text-rose-700">{stats.tasa_respuesta}%</p>
          <p className="text-xs text-rose-600">{stats.total_respondidas}/{stats.total_destinatarios}</p>
        </Card>
      </div>
      
      {/* Promedios por pregunta */}
      {(stats.promedios_por_pregunta || []).length > 0 && (
        <Card className="p-5 border-slate-200" data-testid="promedios-por-pregunta">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-rose-500" /> Promedio por Pregunta
          </h3>
          <div className="space-y-2">
            {stats.promedios_por_pregunta.map((p, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-xs font-mono px-2 py-1 rounded bg-slate-100 text-slate-600 w-10 text-center">P{idx + 1}</span>
                <span className="text-sm text-slate-700 flex-1 truncate">{p.pregunta}</span>
                <div className="flex items-center gap-2 min-w-[140px]">
                  <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${p.promedio >= 4 ? 'bg-emerald-500' : p.promedio >= 3 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${(p.promedio / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-slate-700 w-12 text-right">{p.promedio}/5</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      {stats.porcentaje_detractores > 0 && (
        <Card className="p-3 border-red-200 bg-red-50 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <p className="text-sm text-red-700"><strong>{stats.porcentaje_detractores}% detractores</strong> (≤ 2/5). Se generaron tareas automáticas de "Revisión de Caso" en el Calendario de Touchpoints.</p>
        </Card>
      )}

      {/* AI Resumen */}
      <Card className="p-5 border-purple-200 bg-gradient-to-r from-purple-50 to-rose-50" data-testid="campana-ai-resumen">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-purple-900">Resumen Ejecutivo IA</h3>
            <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">Gemini 3 Flash</Badge>
          </div>
          <Button size="sm" variant="outline" onClick={onGenerarResumen} disabled={savingResumen || stats.total_respondidas === 0} data-testid="generate-ai-resumen-btn">
            <Sparkles className="w-4 h-4 mr-1" />
            {campana.ai_resumen ? 'Regenerar' : 'Generar'}
          </Button>
        </div>
        {campana.ai_resumen ? (
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{campana.ai_resumen}</p>
        ) : (
          <p className="text-sm text-slate-500 italic">
            {stats.total_respondidas === 0
              ? 'Aún no hay respuestas para analizar.'
              : 'Haz clic en "Generar" para que la IA analice las respuestas.'}
          </p>
        )}
      </Card>

      {/* Nube de Palabras */}
      {nube_palabras.length > 0 && (
        <Card className="p-5 border-slate-200" data-testid="nube-palabras">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-rose-500" /> Nube de Palabras
          </h3>
          <div className="flex flex-wrap gap-2">
            {nube_palabras.map((p) => {
              const size = Math.round(12 + (p.count / maxCount) * 24);
              return (
                <span
                  key={p.word}
                  className="px-3 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-medium"
                  style={{ fontSize: `${size}px` }}
                  title={`${p.count} menciones`}
                >
                  {p.word}
                </span>
              );
            })}
          </div>
        </Card>
      )}

      {/* Respuestas */}
      <Card className="p-5 border-slate-200" data-testid="respuestas-list">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-500" /> Respuestas ({respuestas.length})
        </h3>
        {respuestas.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Aún no hay respuestas.</p>
        ) : (
          <div className="space-y-3">
            {respuestas.map((r) => {
              const notas = r.notas || [];
              const notaP1 = notas[0] || 0;
              return (
                <div key={r.id} className={`p-3 rounded-lg border ${notaP1 <= 2 ? 'border-red-200 bg-red-50' : notaP1 >= 4 ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <strong className="text-sm text-slate-800">{r.cliente_nombre}</strong>
                      {r.cliente_empresa && <span className="text-xs text-slate-500">({r.cliente_empresa})</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`w-4 h-4 ${i <= notaP1 ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    {new Date(r.fecha_respondida).toLocaleDateString('es-CL')}
                    {notas.length > 1 && ' · ' + notas.map((n, i) => `P${i + 1}: ${n}/5`).join(' · ')}
                  </p>
                  {r.comentario && <p className="text-sm text-slate-700 italic">"{r.comentario}"</p>}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Pendientes / Links copiables */}
      {pendientes.length > 0 && (
        <Card className="p-5 border-amber-200 bg-amber-50/30" data-testid="pendientes-list">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-600" /> Pendientes ({pendientes.length})
          </h3>
          <p className="text-xs text-slate-600 mb-3">
            Copia y envía manualmente estos links a cada cliente (mientras Resend no esté configurado, este es el modo activo).
          </p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {pendientes.map((p) => (
              <div key={p.token} className="flex items-center justify-between gap-2 p-2 bg-white rounded border border-slate-200">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">{p.cliente_nombre}</p>
                  <p className="text-xs text-slate-500 truncate">{p.cliente_email || 'sin email'}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => onCopyLink(p.token)} data-testid={`copy-link-${p.token}`}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar link
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default PostVenta;
